import { showPwaUpdateStatus, hidePwaUpdateStatus } from './pwaUpdateStatus';
import { useEffect } from 'react';
import { registerSW } from 'virtual:pwa-register';

const UPDATE_CHECK_MS = 60 * 60 * 1000;

/**
 * Keeps installed PWA (Add to Home Screen) on the latest build without manual cache clear.
 * - Checks for a new service worker when the app returns to foreground
 * - Reloads once when the new worker takes control (paired with skipWaiting in vite.config)
 */
export function PwaServiceWorkerUpdate() {
  useEffect(() => {
    hidePwaUpdateStatus();
    if (!import.meta.env.PROD) return;

    let refreshing = false;
    const reloadOnce = () => {
      if (refreshing) return;
      refreshing = true;
      showPwaUpdateStatus();
      window.setTimeout(() => window.location.reload(), 150);
    };

    const onControllerChange = () => reloadOnce();
    navigator.serviceWorker?.addEventListener('controllerchange', onControllerChange);

    let removeVisibilityListener: (() => void) | undefined;
    let removeFocusListener: (() => void) | undefined;
    let intervalId: number | undefined;

    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        reloadOnce();
      },
      onRegisteredSW(_swUrl, registration) {
        if (!registration) return;

        const checkForUpdates = () => {
          void registration.update();
        };

        const onVisible = () => {
          if (document.visibilityState === 'visible') {
            checkForUpdates();
          }
        };

        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', checkForUpdates);
        intervalId = window.setInterval(checkForUpdates, UPDATE_CHECK_MS);

        removeVisibilityListener = () => document.removeEventListener('visibilitychange', onVisible);
        removeFocusListener = () => window.removeEventListener('focus', checkForUpdates);
      },
    });

    return () => {
      navigator.serviceWorker?.removeEventListener('controllerchange', onControllerChange);
      removeVisibilityListener?.();
      removeFocusListener?.();
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
      void updateSW;
    };
  }, []);

  return null;
}
